import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { apiGet, TENANT_TOKEN_KEY } from '../api/client';

type AgentSummary = {
  id: string;
  name: string;
  model: string;
  isActive: boolean;
};

type AgentSelectorContextValue = {
  agents: AgentSummary[];
  selectedAgentId: string | null;
  selectedAgent: AgentSummary | null;
  selectAgent: (id: string) => void;
  loading: boolean;
  refresh: () => Promise<void>;
};

const STORAGE_KEY = 'va_selected_agent_id';

const AgentSelectorContext = createContext<AgentSelectorContextValue>({
  agents: [],
  selectedAgentId: null,
  selectedAgent: null,
  selectAgent: () => {},
  loading: true,
  refresh: async () => {},
});

export function AgentSelectorProvider({ children }: { children: ReactNode }) {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY) || null
  );
  const [loading, setLoading] = useState(true);

  const loadAgents = useCallback(async () => {
    const token = localStorage.getItem(TENANT_TOKEN_KEY);
    if (!token) {
      setAgents([]);
      setLoading(false);
      return;
    }
    try {
      const data = await apiGet<AgentSummary[]>('/api/console/agents', token, {
        showErrorGrowl: false,
      });
      setAgents(data);
      if (data.length === 1) {
        // Un solo agente: siempre queda seleccionado por defecto.
        const only = data[0]!;
        if (selectedAgentId !== only.id) {
          setSelectedAgentId(only.id);
          localStorage.setItem(STORAGE_KEY, only.id);
        }
      } else if (data.length > 0) {
        const currentValid = data.some((a) => a.id === selectedAgentId);
        if (!currentValid) {
          const firstActive = data.find((a) => a.isActive) ?? data[0]!;
          setSelectedAgentId(firstActive.id);
          localStorage.setItem(STORAGE_KEY, firstActive.id);
        }
      }
    } catch {
      // Fail silently — layout will handle auth redirect
    } finally {
      setLoading(false);
    }
  }, [selectedAgentId]);

  useEffect(() => {
    void loadAgents();
  }, [loadAgents]);

  const selectAgent = useCallback((id: string) => {
    setSelectedAgentId(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const selectedAgent = agents.find((a) => a.id === selectedAgentId) ?? null;

  return (
    <AgentSelectorContext.Provider
      value={{ agents, selectedAgentId, selectedAgent, selectAgent, loading, refresh: loadAgents }}
    >
      {children}
    </AgentSelectorContext.Provider>
  );
}

export function useAgentSelector() {
  return useContext(AgentSelectorContext);
}
